// @ts-strict-ignore
import {AssessableItem} from "@models";
import {computed, inject, Injectable, signal} from "@angular/core";
import {AssessablePanelMode} from "@common/components/assessment/utils";
import {AssessableContextService} from "./assessment-context.service";

const SCROLL_DELAY = 500;

@Injectable()
export class AssessableUiService<T extends AssessableItem> {
	// Injections
	private _contextService = inject(AssessableContextService);

	// Properties
	private readonly _mode = signal(AssessablePanelMode.View);
	private readonly _itemInEditMode = signal<string | null>(null);
	private readonly _groupInEditMode = signal<string | null>(null);
	private readonly _itemExpanded = signal<string | null>(null);
	private readonly _groupExpanded = signal<string | null>(null);
	private readonly _disabled = signal<boolean>(false);

	public readonly mode = this._mode.asReadonly();
	public readonly isDisabled = this._disabled.asReadonly();
	public readonly isEditPanel = computed(() => this._mode() === AssessablePanelMode.Edit);
	public readonly isAnswerPanel = computed(() => this._mode() === AssessablePanelMode.Answer);
	public readonly itemInEditMode = this._itemInEditMode.asReadonly();
	public readonly groupInEditMode = this._groupInEditMode.asReadonly();
	public readonly itemExpanded = this._itemExpanded.asReadonly();
	public readonly groupExpanded = this._groupExpanded.asReadonly();

	// Getters-Setters
	public setMode(value: AssessablePanelMode) {
		this._mode.set(value);
	}

	public setDisabled(value: boolean) {
		this._disabled.set(value);
	}

	public handleGroupCollapse() {
		if (this._groupInEditMode() && this._contextService.form.valid) {
			this._groupInEditMode.set(null);
		}
		this._groupExpanded.set(null);
	}

	public handleGroupExpand(groupId: string) {
		if (this._groupInEditMode() && this._contextService.form.valid) {
			this._groupInEditMode.set(null);
		}
		this._groupExpanded.set(groupId);
	}

	public handleItemCollapse() {
		if (this._itemInEditMode()) {
			this._itemInEditMode.set(null);
		}
		this._itemExpanded.set(null);
	}

	public handleItemExpand(itemId: string) {
		this._itemExpanded.set(itemId);
		if (this.isEditPanel()) {
			// Items are always expanded in Edit mode
			this._itemInEditMode.set(itemId);
		}
	}

	public scrollAndEditGroup(groupId: string) {
		this.scrollToElement(groupId, () => {
			this.toggleGroupEditMode(groupId);
		});
	}

	public scrollAndEditItem(groupId: string | null, itemId: string | null) {
		this._groupExpanded.set(null);
		this._itemExpanded.set(null);
		if (groupId) {
			this._groupExpanded.set(groupId);
		}
		if (itemId) {
			this._itemInEditMode.set(itemId);
			this._itemExpanded.set(itemId);
		}

		this.scrollToElement(itemId);
	}

	public scrollToElement(elementId: string, onSuccess?: () => void) {
		let tries = 1;
		const interval = setInterval(() => {
			if (tries > 3) {
				clearInterval(interval);
				return;
			}

			let elementToScrollTo = document.getElementById(elementId);
			if (elementToScrollTo) {
				setTimeout(() => {
					elementToScrollTo.scrollIntoView({
						behavior: "smooth",
						block: "start",
					});
				}, 200);
				clearInterval(interval);
				onSuccess?.();
			}
			tries++;
		}, SCROLL_DELAY);
	}

	public expandFirstGroupAndItem() {
		const assessable = this._contextService.getAssessable();
		if (this._mode() === AssessablePanelMode.Edit) {
			// Open first Group in Edit Panel
			setTimeout(() => {
				if (assessable?.assessableGroups?.length > 0) {
					const firstGroup = assessable.assessableGroups[0];
					this._groupExpanded.set(firstGroup.groupId);

					if (assessable.assessableGroups.length === 1 && firstGroup.items.length === 0) {
						this._groupInEditMode.set(firstGroup.groupId);
					}
				}
			}, 800);
		}
	}

	public toggleGroupEditMode(groupId: string) {
		if (this._groupInEditMode() === groupId) {
			this._groupInEditMode.set(null);
		} else {
			this._groupInEditMode.set(groupId);
			this._groupExpanded.set(groupId);
		}
	}
}
